import {
  EnquirySubmission,
  EnquirySubmitted,
} from '~/common/components/offer-enquiry-form';

/**
 * Sends an offer enquiry.
 *
 * Posts to the site rather than to a lead API directly, because publishing a lead here is a server side
 * concern: it attaches reCAPTCHA, resolves the dealer from the postcode and publishes to SNS, exactly as the
 * existing fixed offer enquiry does. The browser therefore never decides the dealer, the timestamp, the
 * client source or the page URL, and cannot be made to lie about them.
 *
 * The endpoint answers 200 with the lead's externalLeadId on success, and 400 with a message the customer can
 * act on - a failed reCAPTCHA, a required field the server found empty. Anything else is reported as a failure
 * the customer can retry, which is all they can usefully do about a dealer API or an SNS outage.
 *
 * That id is the server's, minted once for the lead it just published, and is reported with the analytics
 * event so a conversion can be tied to the lead Salesforce and CDP received.
 */
export const submitOfferEnquiry = (
  submission: EnquirySubmission
): Promise<EnquirySubmitted> =>
  fetch(`/api/v2/offers/${submission.offerUniqueName}/enquiry`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    cache: 'no-cache',
    body: JSON.stringify(submission),
  }).then(async (response) => {
    if (!response.ok) {
      throw new Error(
        `Offer enquiry was not accepted: ${response.status} ${response.statusText}`
      );
    }

    // The enquiry is already published by the time the body is read, so a body that is missing or not JSON -
    // an older build answering 204, a proxy stripping it - costs the measurement its id and nothing else.
    try {
      const body = await response.json();
      return { externalLeadId: body?.externalLeadId };
    } catch {
      return {};
    }
  });
